import { FC } from 'react';
import classNames from 'classnames';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { enteredPercentageViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { useAppSelector } from '@core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { OverrideField } from './useDiscountRateOverride';
import styles from './discount-rate-override.module.scss';
import {
  CaseStructureItem,
  getInstrumentOverrideIndex,
  getOriginalValue,
} from './discountRateOverrideUtils';
import { CaseDto } from '@app/shared/models/contracts/project-dto';
import { TrancheKey } from '@app/shared/models/contracts/enums/shared-enums';

interface OriginalDiscountRateRowsProps {
  instrumentId: string;
  field: OverrideField;
  casesStructure: CaseStructureItem[];
  initializedCases: CaseDto[];
  findSortedCaseIndex: (caseId: string) => number;
  trancheId?: Nullable<string>;
  trancheIndex?: number;
}

const OriginalDiscountRateRows: FC<OriginalDiscountRateRowsProps> = ({
  instrumentId,
  field,
  casesStructure,
  initializedCases,
  findSortedCaseIndex,
  trancheId,
  trancheIndex,
}) => {
  const { l } = useLocale();
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);

  const isCellOverridden = (caseId: string, forecastIndex: number): boolean => {
    const caseIndex = findSortedCaseIndex(caseId);
    const instrumentIdx = getInstrumentOverrideIndex(
      initializedCases,
      caseIndex,
      forecastIndex,
      instrumentId
    );
    if (instrumentIdx === -1) {
      return false;
    }
    const entry =
      initializedCases[caseIndex].forecasts[forecastIndex].discountRateOverrides[instrumentIdx];
    if (field === 'exitDistributionByTrancheDiscountRateOverrides') {
      const exitKey = trancheId ?? TrancheKey.DefaultTrancheKey;
      return entry.exitDistributionByTrancheDiscountRateOverrides[exitKey] != null;
    }
    return entry[field] != null;
  };

  const renderCells = (trancheId?: Nullable<string>) =>
    casesStructure.map((caseItem) =>
      caseItem.forecasts.map((forecast, forecastIndex) => (
        <td
          key={forecast.forecastId + '-original-' + field + (trancheId ?? '')}
          className={classNames({
            'table-primary__cell--vertical-separator':
              caseItem.forecasts.length - 1 === forecastIndex,
          })}>
          {isCellOverridden(caseItem.caseId, forecastIndex) && (
            <CellValue
              {...enteredPercentageViewRules}
              value={getOriginalValue(
                caseItem.caseId,
                forecast.forecastId,
                field,
                instrumentId,
                results,
                trancheId
              )}
            />
          )}
        </td>
      ))
    );

  const isTranche = trancheId !== undefined && trancheIndex !== undefined;
  const testId = isTranche
    ? `original-${field}-row-${instrumentId}-tranche-${trancheIndex}`
    : `original-${field}-row-${instrumentId}`;

  return (
    <tr data-testid={testId} className={styles['original-row']}>
      <td className="table-primary__cell--vertical-separator table-primary__cell--indentation">
        {l('_OriginalDiscountRates')}
      </td>
      {renderCells(trancheId)}
    </tr>
  );
};

export default OriginalDiscountRateRows;
